function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { default as ReactAsyncSelect } from 'react-select/async';
import styled, { withTheme } from 'styled-components';
import PropTypes from 'prop-types';
import { useToast } from 'granite-admin/core/components/Toast';
import GraniteAPIClient from 'granite-admin/utils/granite-client';
const StyledAsyncSelect = styled(ReactAsyncSelect)`
  .async-select__menu {
    z-index: 5;
  }
  .async-select__control {
    background-color: ${({
  backgroundColor
}) => backgroundColor};
    border-width: 0px;
    box-shadow: none;
    outline: none;
    max-height: 100%;
    overflow: auto;
    &:focus,
    &:hover {
      border-width: 0px;
      box-shadow: none;
      outline: none;
    }
  }
  .async-select__indicators {
    -webkit-align-items: normal;
    -webkit-box-align: normal;
    align-items: normal;
  }
  .async-select__value-container {
    padding: 2px;
  }
`;
const addCustomOptions = (customOptions, data, sortKey) => {
  if (customOptions?.position === 'onTop') {
    return [...customOptions?.options, ...data.list];
  } else if (customOptions?.position === 'onLast') {
    return [...data.list, ...customOptions?.options];
  } else {
    return [...data.list, ...customOptions?.options].sort((a, b) => {
      let obj1 = a[sortKey].toLowerCase(),
        obj2 = b[sortKey].toLowerCase();
      return obj1 > obj2 ? 1 : -1;
    });
  }
};
const AsyncSelect = ({
  fetch,
  theme,
  closeMenuOnSelect,
  onChange,
  maxMenuHeight,
  value,
  queryParams,
  labelKey,
  valueKey,
  isMulti,
  name,
  defaultOptions,
  submenuOverlay,
  configData,
  eventEmitter,
  isClearable,
  setFieldValue,
  checkValidity,
  dataSource,
  searchKey,
  extraData,
  setOptionsData,
  appendPkInLabel,
  customOptions,
  dataModifier,
  ...rest
}) => {
  const {
    errorToast
  } = useToast();
  let opts = useMemo(() => {
    return defaultOptions === true ? [] : defaultOptions;
  }, [defaultOptions]);
  const [options, setOptions] = useState(opts);
  const [mode, setMode] = useState('');
  const abortController = useRef(new AbortController());
  useEffect(() => {
    // on unmount, abort the in-progress API
    return () => abortController?.current.abort();
  }, []);
  useEffect(() => {
    let result = valueKey?.reduce ? 'A' : 'B';
    setMode(result);
  }, [valueKey]);
  useEffect(() => {
    if (value && mode && checkValidity && options.length) {
      if (isMulti) {
        let validOptions = value?.filter(val => {
          let selectedValue = mode === 'A' ? val : val[valueKey.key];
          return options.find(option => selectedValue === option[valueKey.key]);
        });
        validOptions?.length ? setFieldValue(name, validOptions) : setFieldValue(name);
      } else {
        let isValidOptionSelected = options.find(i => {
          let selectedValue = mode === 'A' ? value : value[valueKey.key];
          return i[valueKey?.key] === selectedValue;
        });
        if (!isValidOptionSelected) setFieldValue(name);
      }
    }
  }, [mode, options]);
  const updateConfigOptions = options => {
    let updatedConfig = {
      ...configData
    };
    updatedConfig = {
      ...updatedConfig,
      options
    };
    if (eventEmitter) eventEmitter.emit('UPDATE_FILTER_DATA', {
      name: updatedConfig.name,
      payload: updatedConfig
    });
  };
  const mapOptionsToConfig = options => {
    setOptions(options);
    updateConfigOptions(options);
    return options;
  };
  const fetchFromDataSource = async (url, params, signal) => {
    const {
      data
    } = await GraniteAPIClient('').get(url, {
      params,
      signal
    });
    return {
      list: data.results,
      count: data.count
    };
  };
  const fetchOptions = async (inputValue, callback) => {
    try {
      let params = {
        ...{
          [searchKey]: inputValue,
          page_size: 1000,
          page: 1
        },
        ...queryParams
      };
      let data = dataSource ? await fetchFromDataSource(dataSource, params, abortController?.current.signal) : await fetch(params, extraData, abortController?.current.signal);
      if (customOptions) data.list = addCustomOptions(customOptions, data, labelKey);
      if (dataModifier) data.list = dataModifier(data);
      if (appendPkInLabel) {
        data.list = data.list.map(elem => ({
          ...elem,
          [labelKey]: `${elem[labelKey]} (#${elem.pk})`
        }));
      }
      if (!inputValue) mapOptionsToConfig(data?.list);
      setOptionsData(data?.list);
      callback(data?.list);
    } catch (e) {
      console.log('e @ fetchOptions=>', e);
      if (e?.errors?.non_field !== 'Action Aborted') {
        errorToast(e.errors?.title || 'Failed to load Options');
      }
    }
  };
  const debounce = (func, delay) => {
    let timerId;
    return function (inputValue, callback) {
      clearTimeout(timerId);
      timerId = setTimeout(() => {
        func(inputValue, callback);
      }, delay);
    };
  };
  const loadOptions = useCallback(debounce(fetchOptions, 750), []);
  const setChange = useCallback((option, event) => {
    let arg = mode === 'A' ? option && option[valueKey?.key] : mode === 'B' ? option : null;
    if (isMulti) arg = option;
    if (isMulti && mode === 'A') arg = option?.map(x => x[valueKey.key]);
    if (isMulti && event.action === 'remove-value' && mode === 'A') {
      arg = value.filter(i => i !== event.removedValue[valueKey.key]);
    } else if (isMulti && event.action === 'remove-value' && mode === 'B') {
      arg = value.filter(i => i[valueKey.key] !== event.removedValue[valueKey.key]);
    }
    if (isMulti && !arg) arg = [];
    if (!isMulti && !arg) arg = null;
    onChange(arg);
  }, [mode, valueKey, isMulti, onChange, options]);
  const getOptionLabel = useCallback(option => option[labelKey], [labelKey]);
  const computeDefault = () => {
    if (value !== undefined && value !== null) {
      let returnVal;
      if (isMulti) {
        returnVal = mode === 'A' ? options.filter(option => value?.includes(option[valueKey.key])) : options.filter(option => value?.find(val => val[valueKey.key] === option[valueKey.key]));
      } else {
        returnVal = mode === 'A' ? options.find(i => i[valueKey?.key] === value) : options.find(i => i[valueKey?.key] === value[valueKey.key]);
      }
      return returnVal;
    }
  };
  const isOptionSelected = option => {
    if (value) return value === option[valueKey.key] ? true : false;
  };
  const styles = {
    control: base => ({
      ...base,
      fontSize: 'small'
    }),
    menu: base => ({
      ...base,
      fontSize: 'small',
      background: theme.global.colors['bgColor']
    }),
    option: (provided, state) => ({
      ...provided,
      fontFamily: theme.global.font['family'],
      color: state.isSelected ? '#ffffff' : theme.global.colors['textColor'],
      backgroundColor: state.isSelected ? theme.global.colors['brand'] : state.isFocused ? 'rgba(0, 184, 217, 0.1)' : theme.global.colors['bgColor']
    }),
    singleValue: (base, state) => ({
      ...base,
      color: state.isDisabled ? '#8f96a3' : theme.global.colors['textColor']
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: state.isFocused ? theme.global.colors['brand'] : `${theme.global.colors['brand']}88`
    }),
    menuPortal: base => ({
      ...base,
      zIndex: 999
    }),
    ...(theme?.asyncSelectTheme ?? {})
  };
  return /*#__PURE__*/React.createElement(StyledAsyncSelect, _extends({
    classNamePrefix: "async-select",
    defaultOptions: defaultOptions,
    placeholder: '',
    cacheOptions: true,
    loadOptions: loadOptions,
    isMulti: isMulti,
    menuPlacement: "auto",
    styles: styles,
    closeMenuOnSelect: closeMenuOnSelect ? closeMenuOnSelect : isMulti === true ? false : true,
    isOptionSelected: isOptionSelected,
    closeOnSelect: false,
    onSelectResetsInput: false,
    autoBlur: false,
    onChange: setChange,
    maxMenuHeight: maxMenuHeight,
    isClearable: isClearable,
    isSearchable: true,
    value: computeDefault(),
    getOptionLabel: getOptionLabel,
    menuPortalTarget: submenuOverlay,
    backgroundColor: theme.global.colors['bgColor']
  }, rest));
};
AsyncSelect.propTypes = {
  defaultOptions: PropTypes.any,
  Placeholder: PropTypes.any,
  fetch: PropTypes.func,
  value: PropTypes.any,
  setFieldValue: PropTypes.func,
  onChange: PropTypes.func,
  maxMenuHeight: PropTypes.any,
  queryParams: PropTypes.object,
  mapOptionsToConfig: PropTypes.func,
  labelKey: PropTypes.any,
  valueKey: PropTypes.any,
  isMulti: PropTypes.bool,
  isClearable: PropTypes.bool,
  dataSource: PropTypes.string,
  searchKey: PropTypes.string,
  extraData: PropTypes.any,
  setOptionsData: PropTypes.func,
  theme: PropTypes.object,
  closeMenuOnSelect: PropTypes.bool,
  name: PropTypes.string,
  submenuOverlay: PropTypes.any,
  configData: PropTypes.object,
  eventEmitter: PropTypes.object,
  checkValidity: PropTypes.bool,
  appendPkInLabel: PropTypes.bool,
  customOptions: PropTypes.object,
  dataModifier: PropTypes.func
};
AsyncSelect.defaultProps = {
  maxMenuHeight: 200,
  isMulti: false,
  isClearable: true,
  setFieldValue: () => {},
  dataSource: '',
  searchKey: 'search',
  setOptionsData: () => {},
  checkValidity: true,
  appendPkInLabel: false
};
let AsyncSelectWrapper = withTheme(AsyncSelect);
AsyncSelectWrapper.displayName = 'AsyncSelect';
export default AsyncSelectWrapper;